import * as React from "react";
import { merge, $ } from "glamor";
import { IPricing } from "../../types";
import theme from "styles/theme";
import { formatMessage } from "lib/globalize-util";
import { CellValueChangedEvent } from "ag-grid-community";

const styles = merge(
    {
        textAlign: "right",
        fontFamily: "Lato !important",
    },
    $(" .pinnedCell", {
        textAlign: "right",
    }),
    $(" .quantity", {
        fontSize: "12px",
        fontWeight: "bold",
        fontStretch: "normal",
        fontStyle: "normal",
        lineHeight: "1.5",
        letterSpacing: "0.16px",
        textAlign: "right",
        color: "#585858",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    }),
    $(" .totalPrice", {
        fontSize: "10px",
        fontWeight: "normal",
        fontStretch: "normal",
        fontStyle: "normal",
        lineHeight: "1.6",
        letterSpacing: "0.13px",
        textAlign: "right",
        color: "#8e8e8e",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        paddingTop: "6px !important",
    }),
    $(" .totalRetail", {
        fontSize: "9px",
        fontWeight: "normal",
        fontStretch: "normal",
        fontStyle: "normal",
        lineHeight: "1.78",
        letterSpacing: "0.12px",
        textAlign: "right",
        color: theme.palette.secondaryTextColor,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    }),
    $(" .notAvailable", {
        color: theme.palette.secondaryTextColor,
        fontSize: "12px",
        fontWeight: "normal",
        fontStretch: "normal",
        fontStyle: "normal",
        lineHeight: "1.6",
        letterSpacing: "0.16px",
        textAlign: "center",
    }),
    $(" .cellDisabled", {
        height: "16px",
        textAlign: "right",
    }),
);

export const calculateTotals = (acc, { quantity, wholesale, retail, currency, unitsPerPack = 1 }) => {
    if (quantity <= 0) {
        return;
    }
    acc.totalWholesalePrice += quantity * unitsPerPack * parseFloat(wholesale.toFixed(2));
    acc.totalRetailPrice += quantity * unitsPerPack * parseFloat(retail.toFixed(2));
    acc.quantity += quantity * unitsPerPack;
    acc.currency = currency;
};

export const ItemRenderer = (props: CellValueChangedEvent) => {
    const { node, data, value, column, colDef } = props;
    const colId = column.getColId();
    const { aggData, allLeafChildren } = node;
    const total: IPricing = { totalWholesalePrice: 0, totalRetailPrice: 0, quantity: 0 };
    const totalDates: IPricing = { totalWholesalePrice: 0, totalRetailPrice: 0, quantity: 0 };
    const items = !allLeafChildren ? [] : allLeafChildren.map(({ data: { pricing } }) => pricing);

    items.forEach((element) => {
        const item: any = Object.values(element);
        calculateTotals(totalDates, {
            quantity: element[colId]?.quantity || 0,
            wholesale: element[colId]?.wholesalePrice.amount || 0,
            retail: element[colId]?.retailPrice.amount || 0,
            currency: element[colId]?.currency,
        });
        item.forEach((element) => {
            calculateTotals(total, {
                quantity: element?.quantity || 0,
                wholesale: element?.wholesalePrice.amount || 0,
                retail: element?.retailPrice.amount || 0,
                unitsPerPack: element?.unitsPerPack || 1,
                currency: element.currency,
            });
        });
    });

    const totalQuantity = total.quantity || 0;
    const quantity = data?.pricing ? Object.values(data.pricing).reduce((prev, cur: any) => prev + cur.quantity, 0) : 0;
    const formatter = new Intl.NumberFormat("en-US", {
        style: "decimal",
        minimumFractionDigits: 2,
    });

    if (aggData) {
        if (colId !== "total") {
            const cellClass: any = colDef.cellClass;
            const cellClasses = cellClass(props);
            const isCellEnabled = (Array.isArray(cellClasses) ? cellClasses : [cellClasses]).includes("cell-enabled");
            if (isCellEnabled) {
                return (
                    <div {...styles} className="pinnedCell">
                        <div className="quantity">{aggData[colId]}</div>
                        <div className="totalPrice">
                            {totalDates.currency?.wholesale?.symbol}
                            {formatter.format(totalDates.totalWholesalePrice)}
                        </div>
                        <div className="totalRetail">
                            {totalDates.currency?.retail?.symbol}
                            {formatter.format(totalDates.totalRetailPrice)}
                        </div>
                    </div>
                );
            } else {
                return (
                    <div {...styles} className="pinnedCell">
                        {aggData[colId] > 0 ? (
                            <>
                                <div className="quantity notAvailable cellDisabled">{aggData[colId]}</div>
                                <div className="notAvailable totalPrice"> {formatMessage("Not Available")}</div>
                                <div className="totalRetail cellDisabled"></div>
                            </>
                        ) : (
                            <>
                                <div className="quantity notAvailable cellDisabled"></div>
                                <div className="notAvailable"> {formatMessage("Not Available")}</div>
                                <div className="totalRetail cellDisabled"></div>
                            </>
                        )}
                    </div>
                );
            }
        } else {
            return (
                <div {...styles} className="pinnedCell">
                    <div className="quantity">{totalQuantity}</div>
                    <div className="totalPrice">
                        {total.currency?.wholesale?.symbol}
                        {formatter.format(total.totalWholesalePrice)}
                    </div>
                    <div className="totalRetail">
                        {total.currency?.retail?.symbol}
                        {formatter.format(total.totalRetailPrice)}
                    </div>
                </div>
            );
        }
    }
    if (colId !== "total") {
        return (
            <div {...styles} className="pinnedCell">
                <div className="quantity">{value}</div>
            </div>
        );
    } else {
        return (
            <div {...styles} className="pinnedCell">
                <div className="quantity">{quantity}</div>
            </div>
        );
    }
};
